import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FaUserTie,
  FaHandshake,
  FaLightbulb,
  FaUniversity,
  FaGlobeAfrica,
  FaCheckCircle,
  FaFlask,
  FaGraduationCap,
  FaUsers,
  FaBookOpen,
  FaClipboardCheck,
  FaHeart
} from 'react-icons/fa';
import presentationService, { PresentationSection } from '../../services/presentation.service';

// --- Icon Mapping Helper ---
const iconMap: { [key: string]: React.ReactNode } = {
  FaUserTie: <FaUserTie />,
  FaHandshake: <FaHandshake />,
  FaLightbulb: <FaLightbulb />,
  FaUniversity: <FaUniversity />,
  FaGlobeAfrica: <FaGlobeAfrica />,
  FaCheckCircle: <FaCheckCircle />,
  FaFlask: <FaFlask />,
  FaGraduationCap: <FaGraduationCap />,
  FaUsers: <FaUsers />,
  FaBookOpen: <FaBookOpen />,
  FaClipboardCheck: <FaClipboardCheck />,
  FaHeart: <FaHeart />
};

const getIcon = (iconName: string) => iconMap[iconName] || <FaCheckCircle />;

// --- Animation Variants ---
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] }
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};

const slideInLeft = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] }
  }
};

const slideInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] }
  }
};

// --- Components ---
const MemberCard: React.FC<{ name: string; role: string; isPresident?: boolean }> = ({ name, role, isPresident = false }) => (
  <motion.div
    variants={fadeInUp}
    className={`bg-white p-5 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-center group ${isPresident ? 'lg:max-w-md mx-auto' : ''
      }`}
  >
    <div className="w-16 h-16 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full flex items-center justify-center mx-auto mb-3 text-primary group-hover:scale-110 transition-transform duration-300">
      <FaUserTie size={24} className="group-hover:text-primary-dark transition-colors" />
    </div>
    <h3 className="font-bold text-base text-gray-900 group-hover:text-primary transition-colors mb-1.5">{name}</h3>
    <p className={`font-semibold text-sm ${isPresident ? 'text-primary' : 'text-secondary'} transition-colors`}>{role}</p>
  </motion.div>
);

const ValueCard: React.FC<{ icon: string; title: string }> = ({ icon, title }) => (
  <motion.div
    variants={scaleIn}
    className="flex flex-col items-center justify-center p-6 bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 group"
  >
    <div className="text-gray-300 group-hover:text-primary transition-colors mb-3 text-3xl transform group-hover:scale-110 duration-300">
      {getIcon(icon)}
    </div>
    <span className="font-semibold text-gray-800 text-center text-sm group-hover:text-primary transition-colors leading-tight">{title}</span>
  </motion.div>
);

const ActivityItem: React.FC<{ title: string; children: React.ReactNode; icon: string }> = ({ title, children, icon }) => (
  <motion.div
    variants={fadeInUp}
    className="flex gap-4 p-6 rounded-2xl bg-white hover:shadow-lg transition-all duration-300 group"
  >
    <div className="flex-shrink-0 mt-1">
      <div className="w-12 h-12 bg-primary/5 group-hover:bg-primary/10 rounded-xl flex items-center justify-center text-primary text-lg transition-colors duration-300">
        {getIcon(icon)}
      </div>
    </div>
    <div className="flex-1">
      <h3 className="font-bold text-lg text-gray-900 mb-2 group-hover:text-primary transition-colors">{title}</h3>
      <div className="text-gray-600 leading-relaxed font-light text-sm">
        {children}
      </div>
    </div>
  </motion.div>
);

const Presentation: React.FC = () => {
  const [content, setContent] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const sections = await presentationService.getAllContent();
        const contentMap = sections.reduce((acc, section) => {
          acc[section.section_key] = section.content;
          return acc;
        }, {} as Record<string, any>);
        setContent(contentMap);
      } catch (error) {
        console.error("Failed to load presentation content:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Fallback to empty objects if content is missing prevents crashes
  const hero = content.hero || {};
  const intro = content.intro || {};
  const values = content.values || {};
  const missions = content.missions || {};
  const objectives = content.objectives || {};
  const activities = content.activities || {};
  const bureau = content.bureau || {};

  return (
    <div className="min-h-screen bg-white font-sans selection:bg-primary/20 selection:text-primary-dark">

      {/* Hero Section */}
      <section className="relative bg-white pt-32 pb-20 overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px] opacity-60 translate-x-1/3 -translate-y-1/3"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[80px] opacity-40 -translate-x-1/3 translate-y-1/3"></div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-1.5 rounded-full bg-primary/5 text-primary text-sm font-semibold mb-6 tracking-wide uppercase"
          >
            {hero.badge || 'Notre Organisation'}
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl font-extrabold mb-8 tracking-tight text-gray-900"
          >
            {hero.title || 'A PROPOS DE'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">LA SAPI</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="text-xl md:text-2xl font-light text-gray-500 max-w-3xl mx-auto"
          >
            {hero.subtitle || 'Société Algérienne de Pharmacie Industrielle'}
          </motion.p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="max-w-4xl mx-auto text-left"
          >
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-3xl font-bold text-gray-900 mb-8 relative inline-block"
            >
              {intro.title || 'Qui sommes-nous ?'}
              <span className="absolute -bottom-2 left-0 w-full h-0.5 bg-gradient-to-r from-primary/0 via-primary/50 to-primary/0"></span>
            </motion.h2>
            {intro.paragraphs?.map((paragraph: string, index: number) => (
              <motion.p
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                transition={{ delay: 0.2 + (index * 0.1) }}
                className="text-lg text-gray-600 leading-loose mb-6 font-light"
              >
                {paragraph}
              </motion.p>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Valeurs */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-gray-900 mb-3">{values.title || 'Nos Valeurs'}</motion.h2>
            <motion.p
              variants={fadeIn}
              className="text-gray-500 text-sm">{values.subtitle || 'Les principes qui guident nos actions'}</motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 max-w-6xl mx-auto"
          >
            {values.items?.map((item: any, index: number) => (
              <ValueCard key={index} icon={item.icon} title={item.title} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Missions & Objectifs Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-10 max-w-6xl mx-auto">

            {/* Missions */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={slideInLeft}
              className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300"
            >
              <motion.div
                variants={fadeInUp}
                className="flex items-center gap-4 mb-6 pb-5"
              >
                <div className="p-3 bg-primary/5 rounded-xl text-primary">
                  <FaGlobeAfrica size={22} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{missions.title || 'Nos Missions'}</h2>
              </motion.div>
              <ul className="space-y-4">
                {missions.items?.map((item: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-4 group">
                    <FaCheckCircle className="text-primary/40 group-hover:text-primary transition-colors mt-1.5 flex-shrink-0" size={16} />
                    <span className="text-gray-600 font-light leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Objectifs */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={slideInRight}
              className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300"
            >
              <motion.div
                variants={fadeInUp}
                className="flex items-center gap-4 mb-6 pb-5"
              >
                <div className="p-3 bg-secondary/10 rounded-xl text-secondary">
                  <FaLightbulb size={22} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{objectives.title || 'Nos Objectifs'}</h2>
              </motion.div>
              <ul className="space-y-4">
                {objectives.items?.map((item: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-4 group">
                    <FaCheckCircle className="text-secondary/40 group-hover:text-secondary transition-colors mt-1.5 flex-shrink-0" size={16} />
                    <span className="text-gray-600 font-light leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Activités */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{activities.title || 'Nos Activités'}</h2>
            <div className="w-16 h-1 bg-primary/30 rounded-full mx-auto"></div>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-5"
          >
            {activities.items?.map((item: any, index: number) => (
              <ActivityItem
                key={index}
                title={item.title}
                icon={item.icon}
              >
                {item.description}
              </ActivityItem>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Bureau Section */}
      <section className="py-16 bg-white relative">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{bureau.title || 'Notre Bureau'}</h2>
            <p className="text-gray-500 text-sm">{bureau.subtitle || "L'équipe dirigeante de la SAPI"}</p>
          </motion.div>

          {/* First Row - Présidente (centered) */}
          {bureau.president && (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              className="mb-8"
            >
              <MemberCard name={bureau.president.name} role={bureau.president.role} isPresident={true} />
            </motion.div>
          )}

          {/* Second Row - Vice-présidents */}
          {bureau.vicePresidents?.length > 0 && (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6"
            >
              {bureau.vicePresidents.map((vp: any, idx: number) => (
                <MemberCard key={idx} name={vp.name} role={vp.role} />
              ))}
            </motion.div>
          )}

          {/* Third Row - Other members */}
          {bureau.members?.length > 0 && (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-3 gap-5"
            >
              {bureau.members.map((member: any, idx: number) => (
                <MemberCard key={idx} name={member.name} role={member.role} />
              ))}
            </motion.div>
          )}
        </div>
      </section>

    </div>
  );
};

export default Presentation;
